// Cart layer — ports the old CartProvider/CartToast/PurchasePanel/CartClient
// behaviour onto Shopify's AJAX cart (/cart/add.js, /cart/change.js).
(() => {
  const { routes, strings } = window.mizu;
  const reduced = () => (document.documentElement.dataset.motion || 'static') === 'static';
  const json = (url, body) =>
    fetch(url, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    }).then((r) => (r.ok ? r.json() : Promise.reject(r)));

  // Badge remounted with a key in React → re-trigger the one-shot pulse.
  function setCount(count) {
    const badge = document.querySelector('[data-mizu-cart-badge]');
    if (!badge) return;
    badge.textContent = count;
    badge.hidden = count <= 0;
    badge.classList.remove('badge-pulse');
    void badge.offsetWidth;
    badge.classList.add('badge-pulse');
  }

  let toastTimer;
  function toast(error, message) {
    const el = document.querySelector('[data-mizu-toast]');
    if (!el) return;
    clearTimeout(toastTimer);
    el.querySelector('[data-toast-text]').textContent = message || (error ? strings.addError : strings.added);
    el.querySelector('[data-toast-cta]').hidden = error;
    el.toggleAttribute('data-error', error);
    if (error) el.removeAttribute('href'); else el.setAttribute('href', routes.cart);
    el.classList.remove('is-leaving');
    el.hidden = false;
    if (!reduced()) {
      el.classList.remove('is-entering');
      void el.offsetWidth;
      el.classList.add('is-entering');
    }
    // Long enough to read and tap "לעגלה"; errors stay a little longer.
    toastTimer = setTimeout(() => {
      if (reduced()) return void (el.hidden = true);
      el.classList.add('is-leaving');
      setTimeout(() => { el.hidden = true; el.classList.remove('is-leaving'); }, 200);
    }, error ? 5000 : 4000);
  }

  async function addToCart(id, quantity) {
    try {
      await json(`${routes.cartAdd}.js`, { items: [{ id: Number(id), quantity }] });
      const cart = await json(`${routes.cart}.js`);
      setCount(cart.item_count);
      toast(false);
    } catch (err) {
      toast(true);
      throw err;
    }
  }

  // One request per button at a time: a double tap must not add twice.
  async function busy(btn, label, run) {
    if (btn.dataset.busy) return;
    btn.dataset.busy = '1';
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    const before = btn.textContent;
    if (label) btn.textContent = label;
    try { await run(); } finally {
      delete btn.dataset.busy;
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
      if (label && btn.textContent === label) btn.textContent = before;
    }
  }

  // ProductCard quick add (the button sits inside the card link).
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-mizu-quick-add]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    busy(btn, null, () => addToCart(btn.dataset.mizuQuickAdd, 1)).catch(() => {});
  });

  // PurchasePanel: variant pills, quantity clamp, add-to-cart with "Added ✓".
  function setupPanel(panel) {
    const variants = JSON.parse(panel.querySelector('[data-variants]').textContent);
    const addBtn = panel.querySelector('[data-add-to-cart]');
    const bar = panel.querySelector('[data-buy-bar]');
    const barBtn = panel.querySelector('[data-bar-add]');
    const $ = (sel) => panel.querySelector(sel);
    let sel = variants.find((v) => String(v.id) === addBtn.dataset.variantId) || variants[0];
    let qty = 1;
    let added = false;

    function render() {
      const out = sel.stock <= 0;
      const clamped = Math.min(qty, Math.max(1, sel.stock));
      $('[data-price]').innerHTML = sel.price;
      panel.querySelectorAll('[data-variant]').forEach((b) => {
        const on = b.dataset.variant === String(sel.id);
        b.classList.toggle('border-water', on); b.classList.toggle('bg-water/10', on); b.classList.toggle('text-ink', on);
        b.classList.toggle('border-mist', !on); b.classList.toggle('text-stone', !on); b.classList.toggle('hover:border-stone', !on);
      });
      $('[data-stock-dot]').classList.toggle('bg-mist', out);
      $('[data-stock-dot]').classList.toggle('bg-water', !out);
      $('[data-stock-text]').textContent = out ? panel.dataset.out : panel.dataset.in;
      $('[data-qty-row]').hidden = out;
      $('[data-qty]').textContent = clamped;
      $('[data-qty-minus]').disabled = clamped <= 1;
      $('[data-qty-plus]').disabled = clamped >= sel.stock;
      const label = out ? panel.dataset.out : added ? panel.dataset.added : panel.dataset.add;
      for (const b of [addBtn, barBtn]) {
        if (!b || b.dataset.busy) continue;
        b.disabled = out;
        b.textContent = label;
      }
      if (bar) bar.querySelector('[data-bar-price]').innerHTML = sel.price.replace('text-3xl', 'text-lg');
    }

    panel.querySelectorAll('[data-variant]').forEach((b) =>
      b.addEventListener('click', () => { sel = variants.find((v) => String(v.id) === b.dataset.variant); render(); }));
    $('[data-qty-minus]').addEventListener('click', () => { qty = Math.max(1, qty - 1); render(); });
    $('[data-qty-plus]').addEventListener('click', () => { qty = Math.min(sel.stock, qty + 1); render(); });
    const add = (btn) => {
      if (sel.stock <= 0 || addBtn.dataset.busy || barBtn?.dataset.busy) return;
      const peer = btn === addBtn ? barBtn : addBtn;
      if (peer) peer.disabled = true;
      busy(btn, panel.dataset.adding, () => addToCart(sel.id, Math.min(qty, Math.max(1, sel.stock))))
        .then(() => {
          added = true; render();
          setTimeout(() => { added = false; render(); }, 1600);
        })
        .catch(() => {})
        .finally(() => render());
    };
    addBtn.addEventListener('click', () => add(addBtn));
    barBtn?.addEventListener('click', () => add(barBtn));

    // Mobile buy bar: visible only while the panel's own button is off screen.
    if (bar) {
      const lg = matchMedia('(min-width: 1024px)');
      new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && !lg.matches;
        bar.hidden = !show;
        document.body.classList.toggle('has-buy-bar', show);
      }).observe(addBtn);
    }
    render();
  }
  document.querySelectorAll('[data-mizu-purchase]').forEach(setupPanel);

  // Cart page: quantity steps / remove → /cart/change.js, then re-render the
  // section in place through the Section Rendering API.
  const cartBox = document.querySelector('[data-mizu-cart]');
  async function changeLine(line, quantity) {
    if (cartBox.getAttribute('aria-busy')) return; // one change at a time; responses never race
    cartBox.setAttribute('aria-busy', 'true');
    try {
      const cart = await json(`${routes.cartChange}.js`, { line: Number(line), quantity });
      setCount(cart.item_count);
      const res = await fetch(`${location.pathname}?section_id=${cartBox.dataset.sectionId}`);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const fresh = doc.querySelector('[data-mizu-cart]');
      if (fresh) cartBox.innerHTML = fresh.innerHTML;
    } catch {
      toast(true, strings.updateError);
    } finally {
      cartBox.removeAttribute('aria-busy');
    }
  }
  cartBox?.addEventListener('click', (e) => {
    const row = e.target.closest('[data-line]');
    if (!row) return;
    const step = e.target.closest('[data-cart-step]');
    if (step) changeLine(row.dataset.line, Number(row.dataset.qty) + Number(step.dataset.cartStep));
    if (e.target.closest('[data-cart-remove]')) changeLine(row.dataset.line, 0);
  });
})();
